// Date utility functions for trips

import { TripDates } from '../api/tripsApi';

/**
 * Parse a date-ONLY string ("YYYY-MM-DD") as a LOCAL calendar date.
 * `new Date("2026-08-25")` parses as UTC midnight, so in any timezone behind UTC
 * it renders as the previous day. Trip dates are calendar dates (no time/zone),
 * so we build the Date from its parts in local time. Full datetime strings
 * (with a "T") fall through to the native parser.
 */
export const parseLocalDate = (dateStr: string): Date => {
  const s = String(dateStr).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(s);
};

/**
 * Format a Date's LOCAL calendar day + an HH:MM time as a naive wall-clock
 * string ("YYYY-MM-DDTHH:MM:00", no timezone). This is the canonical activity
 * datetime format — it matches what the agent/chat path emits, so both write
 * paths agree and render at the same clock time (never UTC-shifted).
 */
export const toNaiveDateTime = (date: Date, hours: number, minutes: number): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hours)}:${pad(minutes)}:00`;
};

export const formatTripDates = (dates: TripDates | undefined): string => {
  if (!dates) return 'No dates set';
  
  const { startDate, endDate, startOffsetDaysPositive, startOffsetDaysNegative, endOffsetDaysPositive, endOffsetDaysNegative } = dates;
  
  if (!startDate || !endDate) return 'No dates set';
  
  // Calculate actual dates with offsets
  const start = parseLocalDate(startDate);
  if (startOffsetDaysPositive) {
    start.setDate(start.getDate() + startOffsetDaysPositive);
  } else if (startOffsetDaysNegative) {
    start.setDate(start.getDate() - startOffsetDaysNegative);
  }

  const end = parseLocalDate(endDate);
  if (endOffsetDaysPositive) {
    end.setDate(end.getDate() + endOffsetDaysPositive);
  } else if (endOffsetDaysNegative) {
    end.setDate(end.getDate() - endOffsetDaysNegative);
  }
  
  const startFormatted = formatDate(startDate);
  const endFormatted = formatDate(endDate);
  
  if (startFormatted === endFormatted) {
    return startFormatted;
  }
  
  return `${startFormatted} - ${endFormatted}`;
};

export const formatDateDisplay = (dateStr: string): string => {
  return formatDate(dateStr);
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = parseLocalDate(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const calculateDaysRemaining = (dates: TripDates | undefined): number | null => {
  if (!dates || !dates.startDate) return null;

  const start = parseLocalDate(dates.startDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  
  const diffTime = start.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : null;
};

